import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PersonalInfoForm = ({ user, onSave }) => {
  const [formData, setFormData] = useState({
    firstName: user?.firstName || 'John',
    lastName: user?.lastName || 'Smith',
    email: user?.email || 'john.smith@email.com',
    phone: user?.phone || '+1 555-0123',
    dateOfBirth: user?.dateOfBirth || '1985-06-15',
    nationality: user?.nationality || 'US',
    address: user?.address || '123 Main Street',
    city: user?.city || 'New York',
    state: user?.state || 'NY',
    postalCode: user?.postalCode || '10001',
    country: user?.country || 'US',
    occupation: user?.occupation || 'Software Engineer',
    sourceOfFunds: user?.sourceOfFunds || 'employment'
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const countryOptions = [
    { value: 'US', label: 'United States' },
    { value: 'UK', label: 'United Kingdom' },
    { value: 'CA', label: 'Canada' },
    { value: 'AU', label: 'Australia' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'IT', label: 'Italy' },
    { value: 'ES', label: 'Spain' },
    { value: 'NL', label: 'Netherlands' },
    { value: 'BE', label: 'Belgium' }
  ];

  const sourceOfFundsOptions = [
    { value: 'employment', label: 'Employment/Salary' },
    { value: 'business', label: 'Business Income' },
    { value: 'investment', label: 'Investment Returns' },
    { value: 'savings', label: 'Personal Savings' },
    { value: 'inheritance', label: 'Inheritance' },
    { value: 'gift', label: 'Gift' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      onSave(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || 'John',
      lastName: user?.lastName || 'Smith',
      email: user?.email || 'john.smith@email.com',
      phone: user?.phone || '+1 555-0123',
      dateOfBirth: user?.dateOfBirth || '1985-06-15',
      nationality: user?.nationality || 'US',
      address: user?.address || '123 Main Street',
      city: user?.city || 'New York',
      state: user?.state || 'NY',
      postalCode: user?.postalCode || '10001',
      country: user?.country || 'US',
      occupation: user?.occupation || 'Software Engineer',
      sourceOfFunds: user?.sourceOfFunds || 'employment'
    });
    setIsEditing(false);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="User" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Personal Information</h2>
            <p className="text-sm text-muted-foreground">Manage your personal details and contact information</p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            onClick={() => setIsEditing(true)}
            iconName="Edit"
            iconPosition="left"
            iconSize={16}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground mb-4">Personal Details</h3>
          
          <Input
            label="First Name"
            type="text"
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            disabled={!isEditing}
            required
          />

          <Input
            label="Last Name"
            type="text"
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            disabled={!isEditing}
            required
          />

          <Input
            label="Email Address"
            type="email"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            disabled={!isEditing}
            required
          />

          <Input
            label="Phone Number"
            type="tel"
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            disabled={!isEditing}
            required
          />

          <Input
            label="Date of Birth"
            type="date"
            value={formData?.dateOfBirth}
            onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
            disabled={!isEditing}
            required
          />

          <Select
            label="Nationality"
            options={countryOptions}
            value={formData?.nationality}
            onChange={(value) => handleInputChange('nationality', value)}
            disabled={!isEditing}
            required
          />
        </div>

        {/* Address & Additional Info */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground mb-4">Address Information</h3>
          
          <Input
            label="Street Address"
            type="text"
            value={formData?.address}
            onChange={(e) => handleInputChange('address', e?.target?.value)}
            disabled={!isEditing}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="City"
              type="text"
              value={formData?.city}
              onChange={(e) => handleInputChange('city', e?.target?.value)}
              disabled={!isEditing}
              required
            />

            <Input
              label="State/Province"
              type="text"
              value={formData?.state}
              onChange={(e) => handleInputChange('state', e?.target?.value)}
              disabled={!isEditing}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Postal Code"
              type="text"
              value={formData?.postalCode}
              onChange={(e) => handleInputChange('postalCode', e?.target?.value)}
              disabled={!isEditing}
              required
            />

            <Select
              label="Country"
              options={countryOptions}
              value={formData?.country}
              onChange={(value) => handleInputChange('country', value)}
              disabled={!isEditing}
              required
            />
          </div>

          <Input
            label="Occupation"
            type="text"
            value={formData?.occupation}
            onChange={(e) => handleInputChange('occupation', e?.target?.value)}
            disabled={!isEditing}
            required
          />

          <Select
            label="Source of Funds"
            options={sourceOfFundsOptions}
            value={formData?.sourceOfFunds}
            onChange={(value) => handleInputChange('sourceOfFunds', value)}
            disabled={!isEditing}
            required
          />
        </div>
      </div>
      {isEditing && (
        <div className="flex items-center justify-end space-x-4 mt-6 pt-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={isSaving}
            iconName="Save"
            iconPosition="left"
            iconSize={16}
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};

export default PersonalInfoForm;